import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.tsx';
import { Download, ExternalLink, HelpCircle } from 'lucide-react';
import { BookingSummaryConsentDto } from '@/features/public/bookingSummary/types/BookingPublicSummaryDto.ts';
import {
  consentStatusIcons,
  consentStatusLabels,
  consentStatusTones,
} from '@/features/dashboard/consentTemplates/types/ConsentStatus.utils.ts';
import StatusDot from '@/components/ui/StatusDot.tsx';
import { ConsentType } from '@/features/dashboard/consentTemplates/types/ConsentTemplateType.ts';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { useAcceptConsent } from '@/features/public/bookingSummary/hooks/useAcceptConsent.ts';
import bookingSummaryApi from '@/features/public/bookingSummary/api/bookingSummaryApi.ts';
import WithdrawConsentInfoModal from '@/features/public/bookingSummary/components/WithdrawConsentInfoModal.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip.tsx';
import { consentSummaryAction } from '@/features/public/bookingSummary/components/consentSummaryAction.ts';
import { BookingStatus } from '@/types/BookingStatus.ts';

interface AcceptedConsentsCardProps {
  consents: BookingSummaryConsentDto[];
  orgSlug: string;
  token: string;
  organizerName: string;
  bookingStatus: BookingStatus;
}

const AcceptedConsentsCard = ({
  consents,
  orgSlug,
  token,
  organizerName,
  bookingStatus,
}: AcceptedConsentsCardProps) => {
  const { mutate, isPending } = useAcceptConsent();
  const [acceptingConsentId, setAcceptingConsentId] = useState<string | null>(
    null,
  );
  const [withdrawInfoOpen, setWithdrawInfoOpen] = useState(false);

  const handleAccept = (tourConsentId: string) => {
    setAcceptingConsentId(tourConsentId);
    mutate({ orgSlug, tourConsentId, token });
  };

  // Backend odmawia wycofania zgody anulowanej rezerwacji, więc instrukcja byłaby nieprawdziwa.
  const canWithdraw = bookingStatus !== BookingStatus.CANCELLED;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Zgody i dokumenty</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {consents.map((consent) => (
          <div
            key={consent.tourConsentId}
            className="flex items-start justify-between gap-2"
          >
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="text-sm">{consent.name}</span>
              <StatusDot
                icon={consentStatusIcons[consent.status]}
                tone={consentStatusTones[consent.status]}
                label={consentStatusLabels[consent.status]}
                className="gap-1.5 text-xs"
              />
            </div>
            {consentSummaryAction(consent, bookingStatus) === 'SIGN' && (
              <ActionButton
                tone="accent"
                size="xs"
                isLoading={
                  isPending && acceptingConsentId === consent.tourConsentId
                }
                onClick={() => handleAccept(consent.tourConsentId)}
              >
                Akceptuj
              </ActionButton>
            )}
            {consentSummaryAction(consent, bookingStatus) === 'DOCUMENT' && (
              <div className="flex items-start gap-2">
                {consent.acceptedConsentId && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <a
                        href={bookingSummaryApi.getConsentFileUrl(
                          orgSlug,
                          consent.acceptedConsentId,
                          token,
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-0.5 inline-flex shrink-0 cursor-pointer text-neutral-500 hover:text-neutral-700"
                        aria-label={
                          consent.type === ConsentType.LINK
                            ? 'Otwórz link'
                            : 'Pobierz dokument'
                        }
                      >
                        {consent.type === ConsentType.LINK ? (
                          <ExternalLink size={16} />
                        ) : (
                          <Download size={16} />
                        )}
                      </a>
                    </TooltipTrigger>
                    <TooltipContent>
                      {consent.type === ConsentType.LINK
                        ? 'Otwórz link'
                        : 'Pobierz dokument'}
                    </TooltipContent>
                  </Tooltip>
                )}
                {canWithdraw && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => setWithdrawInfoOpen(true)}
                        className="mt-0.5 inline-flex shrink-0 cursor-pointer text-neutral-500 hover:text-neutral-700"
                        aria-label="Jak wycofać akceptację dokumentu?"
                      >
                        <HelpCircle size={16} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Jak wycofać akceptację?</TooltipContent>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        ))}
      </CardContent>
      {withdrawInfoOpen && (
        <WithdrawConsentInfoModal
          organizerName={organizerName}
          onClose={() => setWithdrawInfoOpen(false)}
        />
      )}
    </Card>
  );
};

export default AcceptedConsentsCard;
